import React, { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Table } from 'antd';
import {
    CalendarOutlined,
    ExportOutlined,
    PlusOutlined,
} from '@ant-design/icons';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import './homeOverview.scss';

/** Matches app header / primary brand blue (see App.scss) */
const PRIMARY_BLUE = '#006ac3';
const GRAY_BAR = '#d1d5db';

const baseChart = {
    credits: { enabled: false },
    exporting: { enabled: false },
    legend: { enabled: false },
    tooltip: { enabled: false },
};

const activityRows = [
    {
        key: '1',
        surveyName: 'Youth Readiness Q1',
        due: 'Due Mar 31',
        program: 'Work Ready',
        status: 'progress',
        statusLabel: 'In Progress',
        responses: '412 / 500',
    },
    {
        key: '2',
        surveyName: 'Partner Feedback',
        due: 'Due Mar 15',
        program: 'Partnerships',
        status: 'complete',
        statusLabel: 'Complete',
        responses: '88 / 88',
    },
    {
        key: '3',
        surveyName: 'Baseline Assessment',
        due: 'Due Feb 28',
        program: 'Employment',
        status: 'overdue',
        statusLabel: 'Overdue',
        responses: '201 / 350',
    },
];

const ADD_SURVEY_PATH = '/survey/add';

const HomeOverview = () => {
    const navigate = useNavigate();
    const goToAddSurvey = useCallback(() => {
        navigate(ADD_SURVEY_PATH);
    }, [navigate]);

    const impactOptions = useMemo(
        () => ({
            ...baseChart,
            chart: {
                type: 'area',
                height: 100,
                backgroundColor: 'transparent',
                margin: [4, 4, 4, 4],
                spacing: [0, 0, 0, 0],
            },
            title: { text: null },
            xAxis: {
                visible: false,
                minPadding: 0,
                maxPadding: 0,
            },
            yAxis: {
                visible: false,
                minPadding: 0,
                maxPadding: 0,
            },
            plotOptions: {
                area: {
                    fillColor: {
                        linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
                        stops: [
                            [0, 'rgba(0, 106, 195, 0.45)'],
                            [1, 'rgba(0, 106, 195, 0)'],
                        ],
                    },
                    lineColor: PRIMARY_BLUE,
                    lineWidth: 2,
                    marker: { enabled: false },
                    states: { hover: { enabled: false } },
                },
                series: { animation: false },
            },
            series: [
                {
                    data: [72, 74, 76, 79, 81, 85],
                },
            ],
        }),
        []
    );

    const peopleOptions = useMemo(
        () => ({
            ...baseChart,
            chart: {
                type: 'line',
                height: 100,
                backgroundColor: 'transparent',
                margin: [4, 4, 4, 4],
            },
            title: { text: null },
            xAxis: { visible: false },
            yAxis: { visible: false },
            plotOptions: {
                line: {
                    color: PRIMARY_BLUE,
                    lineWidth: 2,
                    marker: { enabled: false },
                    states: { hover: { enabled: false } },
                },
                series: { animation: false },
            },
            series: [
                {
                    data: [42000, 45000, 48000, 51000, 53000, 56000],
                },
            ],
        }),
        []
    );

    const dataCollectedOptions = useMemo(
        () => ({
            ...baseChart,
            chart: {
                type: 'column',
                height: 100,
                backgroundColor: 'transparent',
                margin: [4, 4, 4, 4],
            },
            title: { text: null },
            xAxis: { visible: false },
            yAxis: { visible: false },
            plotOptions: {
                column: {
                    borderWidth: 0,
                    borderRadius: 3,
                    pointPadding: 0.12,
                    groupPadding: 0.14,
                },
                series: { animation: false },
            },
            series: [
                {
                    data: [
                        { y: 52, color: GRAY_BAR },
                        { y: 58, color: GRAY_BAR },
                        { y: 64, color: GRAY_BAR },
                        { y: 75, color: PRIMARY_BLUE },
                    ],
                },
            ],
        }),
        []
    );

    const columns = [
        {
            title: 'Survey name',
            dataIndex: 'surveyName',
            key: 'surveyName',
            render: (text, record) => (
                <div>
                    <div className="home-overview__survey-name">{text}</div>
                    <div className="home-overview__survey-due">{record.due}</div>
                </div>
            ),
        },
        {
            title: 'Program',
            dataIndex: 'program',
            key: 'program',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (_, record) => {
                const cls =
                    record.status === 'progress'
                        ? 'home-overview__status-dot--progress'
                        : record.status === 'complete'
                          ? 'home-overview__status-dot--complete'
                          : 'home-overview__status-dot--overdue';
                return (
                    <span>
                        <span className={`home-overview__status-dot ${cls}`} />
                        {record.statusLabel}
                    </span>
                );
            },
        },
        {
            title: 'Responses',
            dataIndex: 'responses',
            key: 'responses',
            align: 'right',
        },
    ];

    return (
        <div className="home-overview">
            <header className="home-overview__header">
                <div>
                    <p className="home-overview__org-label">Organization</p>
                    <h1 className="home-overview__title">Context Research Group</h1>
                    <p className="home-overview__subtitle">Impact overview · Q1 2025</p>
                </div>
                <div className="home-overview__actions">
                    <Button className="home-overview__date-btn" icon={<CalendarOutlined />}>
                        Jan – Mar 2025
                    </Button>
                    <Button className="home-overview__export-btn" icon={<ExportOutlined />}>
                        Export
                    </Button>
                    <Button
                        type="primary"
                        className="home-overview__primary-btn"
                        icon={<PlusOutlined />}
                        onClick={goToAddSurvey}
                    >
                        Add Survey
                    </Button>
                </div>
            </header>

            <div className="home-overview__metrics">
                <div className="home-overview__metric-card" style={{ minWidth: '100%' }}>
                    <p className="home-overview__metric-label">Impact score</p>
                    <div className="home-overview__metric-top">
                        <div className="home-overview__metric-value">85%</div>
                        <span className="home-overview__trend-pill">+ 4.2%</span>
                    </div>
                    <p className="home-overview__metric-sub">vs 81.4% last quarter</p>
                    <div className="home-overview__chart-wrap">
                        <HighchartsReact highcharts={Highcharts} options={impactOptions} />
                    </div>
                </div>

                <div className="home-overview__metric-card" style={{ minWidth: '100%' }}>
                    <p className="home-overview__metric-label">People reached</p>
                    <div className="home-overview__metric-top">
                        <div className="home-overview__metric-value">56,000</div>
                        <span className="home-overview__trend-pill">+ 12.8%</span>
                    </div>
                    <p className="home-overview__metric-sub">vs 49,628 last quarter</p>
                    <div className="home-overview__chart-wrap">
                        <HighchartsReact highcharts={Highcharts} options={peopleOptions} />
                    </div>
                </div>

                <div className="home-overview__metric-card" style={{ minWidth: '100%' }}>
                    <p className="home-overview__metric-label">Data collected</p>
                    <div className="home-overview__metric-top">
                        <div className="home-overview__metric-value">75%</div>
                        <span className="home-overview__trend-pill">+ 7.1%</span>
                    </div>
                    <p className="home-overview__metric-sub">completion rate this quarter</p>
                    <div className="home-overview__chart-wrap">
                        <HighchartsReact highcharts={Highcharts} options={dataCollectedOptions} />
                    </div>
                </div>
            </div>

            <div className="home-overview__bottom">
                <div className="home-overview__collect-card">
                    <h2 className="home-overview__collect-title">Collect Data</h2>
                    <p className="home-overview__collect-desc">
                        Create and distribute surveys to your program participants and partners.
                    </p>
                    <div className="home-overview__collect-btns">
                        <Button
                            type="primary"
                            className="home-overview__collect-primary"
                            icon={<PlusOutlined />}
                            block
                            onClick={goToAddSurvey}
                        >
                            New Survey
                        </Button>
                    </div>
                </div>

                <div className="home-overview__activity-card">
                    <div className="home-overview__activity-head">
                        <h2 className="home-overview__activity-title">Recent Activity</h2>
                        <span className="home-overview__view-all" role="presentation">
                            View all →
                        </span>
                    </div>

                    <div className="home-overview__stats-row">
                        <div className="home-overview__stat-box">
                            <div className="home-overview__stat-label home-overview__stat-label--progress">
                                In progress
                            </div>
                            <div className="home-overview__stat-num">8</div>
                        </div>
                        <div className="home-overview__stat-box">
                            <div className="home-overview__stat-label home-overview__stat-label--green">
                                Complete
                            </div>
                            <div className="home-overview__stat-num">34</div>
                        </div>
                        <div className="home-overview__stat-box">
                            <div className="home-overview__stat-label home-overview__stat-label--red">
                                Overdue
                            </div>
                            <div className="home-overview__stat-num">3</div>
                        </div>
                    </div>

                    <Table
                        className="home-overview-table"
                        columns={columns}
                        dataSource={activityRows}
                        pagination={false}
                        size="middle"
                    />
                </div>
            </div>
        </div>
    );
};

export default HomeOverview;
